import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: {
      canCreateCases: false,
      canEditCases: false,
      canDeleteCases: false,
      canArchiveCases: false,
      canViewAllCases: false,
      canAssignCases: false,
      canSetCaseDeadlines: false,
      canSetCaseAdminViewOnly: false,
      canViewPendingCasesPerInvestigator: false,
      canCreateTasks: false,
      canAssignTasks: false,
      canSetTaskDeadlines: false,
      canCollaborateOnCases: false,
      canUploadEvidence: false,
      canViewEvidence: false,
      canDeleteEvidence: false,
      canManageUsers: false,
      canCreateRoles: false,
      canAssignRoles: false,
      canManageSettings: false,
      canViewAuditLogs: false,
      canManageChainOfCustody: false,
      canViewReports: false,
      canExportReports: false,
      canSelfAssignMediumCases: false,
      canSelfAssignLowCases: false,
    }
  });

  useEffect(() => {
    if (user?.role === 'Administrator') {
      fetchRoles();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data.data);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permission) => {
    setRoleForm({
      ...roleForm,
      permissions: {
        ...roleForm.permissions,
        [permission]: !roleForm.permissions[permission]
      }
    });
  };

  const handleSaveRole = async () => {
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole._id}`, roleForm);
        toast.success('Role updated successfully');
      } else {
        await api.post('/roles', roleForm);
        toast.success('Role created successfully');
      }
      setShowRoleForm(false);
      setEditingRole(null);
      setRoleForm({
        name: '',
        description: '',
        permissions: {
          canCreateCases: false,
          canEditCases: false,
          canDeleteCases: false,
          canArchiveCases: false,
          canViewAllCases: false,
          canAssignCases: false,
          canSetCaseDeadlines: false,
          canSetCaseAdminViewOnly: false,
          canViewPendingCasesPerInvestigator: false,
          canCreateTasks: false,
          canAssignTasks: false,
          canSetTaskDeadlines: false,
          canCollaborateOnCases: false,
          canUploadEvidence: false,
          canViewEvidence: false,
          canDeleteEvidence: false,
          canManageUsers: false,
          canCreateRoles: false,
          canAssignRoles: false,
          canManageSettings: false,
          canViewAuditLogs: false,
          canManageChainOfCustody: false,
          canViewReports: false,
          canExportReports: false,
          canSelfAssignMediumCases: false,
          canSelfAssignLowCases: false,
        }
      });
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save role');
    }
  };

  const handleEditRole = (role) => {
    if (role.isSystemRole) {
      toast.error('Cannot edit system roles');
      return;
    }
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions
    });
    setShowRoleForm(true);
  };

  const handleDeleteRole = async (role) => {
    if (role.isSystemRole) {
      toast.error('Cannot delete system roles');
      return;
    }
    if (window.confirm(`Are you sure you want to delete role "${role.name}"?`)) {
      try {
        await api.delete(`/roles/${role._id}`);
        toast.success('Role deleted successfully');
        fetchRoles();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete role');
      }
    }
  };

  if (user?.role !== 'Administrator') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Settings</h1>
        <p className="text-gray-600">You don't have permission to access settings.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const permissionGroups = [
    {
      title: 'Case Management',
      permissions: [
        'canCreateCases',
        'canEditCases',
        'canDeleteCases',
        'canArchiveCases',
        'canViewAllCases',
        'canAssignCases',
        'canSetCaseDeadlines',
        'canSetCaseAdminViewOnly',
        'canViewPendingCasesPerInvestigator'
      ]
    },
    {
      title: 'Task Management',
      permissions: [
        'canCreateTasks',
        'canAssignTasks',
        'canSetTaskDeadlines',
        'canCollaborateOnCases'
      ]
    },
    {
      title: 'Evidence Management',
      permissions: [
        'canUploadEvidence',
        'canViewEvidence',
        'canDeleteEvidence'
      ]
    },
    {
      title: 'User & Role Management',
      permissions: [
        'canManageUsers',
        'canCreateRoles',
        'canAssignRoles'
      ]
    },
    {
      title: 'System Management',
      permissions: [
        'canManageSettings',
        'canViewAuditLogs',
        'canManageChainOfCustody'
      ]
    },
    {
      title: 'Reports',
      permissions: [
        'canViewReports',
        'canExportReports'
      ]
    },
    {
      title: 'Self-Assignment',
      permissions: [
        'canSelfAssignMediumCases',
        'canSelfAssignLowCases'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage roles and permissions</p>
        </div>
        {!showRoleForm && (
          <button
            onClick={() => {
              setShowRoleForm(true);
              setEditingRole(null);
              setRoleForm({
                name: '',
                description: '',
                permissions: {
                  canCreateCases: false,
                  canEditCases: false,
                  canDeleteCases: false,
                  canArchiveCases: false,
                  canViewAllCases: false,
                  canAssignCases: false,
                  canSetCaseDeadlines: false,
                  canSetCaseAdminViewOnly: false,
                  canViewPendingCasesPerInvestigator: false,
                  canCreateTasks: false,
                  canAssignTasks: false,
                  canSetTaskDeadlines: false,
                  canCollaborateOnCases: false,
                  canUploadEvidence: false,
                  canViewEvidence: false,
                  canDeleteEvidence: false,
                  canManageUsers: false,
                  canCreateRoles: false,
                  canAssignRoles: false,
                  canManageSettings: false,
                  canViewAuditLogs: false,
                  canManageChainOfCustody: false,
                  canViewReports: false,
                  canExportReports: false,
                  canSelfAssignMediumCases: false,
                  canSelfAssignLowCases: false,
                }
              });
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create New Role</span>
          </button>
        )}
      </div>

      {showRoleForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </h2>
            <button
              onClick={() => {
                setShowRoleForm(false);
                setEditingRole(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={roleForm.name}
                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter role name"
                disabled={editingRole?.isSystemRole}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter role description"
                disabled={editingRole?.isSystemRole}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Permissions
              </label>
              <div className="space-y-6">
                {permissionGroups.map((group) => (
                  <div key={group.title} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">{group.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {group.permissions.map((permission) => (
                        <label
                          key={permission}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={roleForm.permissions[permission]}
                            onChange={() => handlePermissionChange(permission)}
                            disabled={editingRole?.isSystemRole}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {permission.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowRoleForm(false);
                  setEditingRole(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRole}
                disabled={!roleForm.name || editingRole?.isSystemRole}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {editingRole ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roles List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">System Roles</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {roles.filter(r => r.isSystemRole).map((role) => (
            <div key={role._id} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                  {role.description && (
                    <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                  )}
                  <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    System Role
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Roles */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Custom Roles</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {roles.filter(r => !r.isSystemRole).length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No custom roles created yet</p>
            </div>
          ) : (
            roles.filter(r => !r.isSystemRole).map((role) => (
              <div key={role._id} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                    {role.description && (
                      <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditRole(role)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
